// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Alert, AlertTitle, Button, Divider,
  TextField, MenuItem, Tooltip, LinearProgress, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VerifiedIcon from '@mui/icons-material/Verified';
import store from './store';
import { PICKLISTS } from './schema';
import { triggerDownload } from './exports';

// ---------------------------------------------------------------------------
// Primordia — Technology Obsolescence & End-of-Support Risk.
//
// Why this screen exists: a real LeanIX tenant models obsolescence as four
// numbers — aggregated risk, mitigated risk %, unaddressed risk % and MISSING
// DATA %. The fourth is the one that makes the other three trustworthy. Without
// it a report says "82% of your estate is supported" while quietly meaning "82%
// of the quarter of the estate I could actually read". That sentence has ended
// more than one migration budget.
//
// The honesty rules this file is built around, in priority order:
//
//   1. NEVER infer a date from an ambiguous OS string. VMware's guest-OS
//      descriptor is a *configuration hint*, not an inventory. "Microsoft
//      Windows Server 2016 or later" spans four server releases whose support
//      dates are seven years apart. "Ubuntu Linux" names no release at all.
//      "Other (32-bit)" names nothing. Those records are UNKNOWN and they stay
//      UNKNOWN. Picking the middle of a range would produce a number that looks
//      like knowledge and is not.
//
//   2. NEVER present an inferred date as vendor-confirmed. A vendor-supplied
//      end-of-support date on the record always beats this file's reference
//      table, and every row says on its face which one produced its date. When
//      a client challenges a number — and they do — the answer has to be
//      "that came from your CMDB field osEos" or "that came from Primordia's
//      reference table, entry Windows Server 2012, which you should verify",
//      never a shrug.
//
//   3. NEVER let unknowns disappear into a reassuring number. Unknown is a
//      first-class classification with its own count and its own share of the
//      estate. It is never folded into "Supported". A server whose OS cannot be
//      dated is not safe; it is unread.
//
//   4. ALWAYS show what share of the estate cannot be assessed. Missing data %
//      is printed next to aggregated risk everywhere aggregated risk appears,
//      including in the CSV export header, because aggregated risk is computed
//      only over the assessable records and is therefore an UNDERSTATEMENT by
//      exactly the amount of the missing-data figure.
//
// The reference table below is Primordia's own working data, assembled from
// published vendor lifecycle pages. It is offered as a starting point for an
// administrator to verify and correct — it is NOT a vendor feed, it carries no
// entitlement information (ESU, ELS, LTSS and extended-support contracts all
// move these dates for money), and it is dated so a stale copy is visible.
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const GREEN = '#2E7D32';
const AMBER = '#B26A00';
const RED = '#C62828';
const PURPLE = '#6A1B9A';
const DASH = '—';

const DAY_MS = 86400000;
const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 200;
const MAX_GROUP_ROWS = 40; // rendered rows in a breakdown; counts cover the full set

// When this reference data was last reviewed. Printed on screen and in every
// export, so a copy that has gone stale announces itself instead of ageing
// silently into a wrong answer.
const REFERENCE_REVIEWED = '2026-08';

// ---------------------------------------------------------------------------
// 1. THE REFERENCE TABLE
//
// Ordered — the first entry whose pattern matches the normalised OS string
// wins, so specific releases must precede their family fallbacks. Every entry
// carries a `source` note; entries flagged `ambiguous` deliberately carry NO
// date and resolve to Unknown.
// ---------------------------------------------------------------------------
export const EOL_REFERENCE = [
  // ---- Windows Server ----
  { id: 'ws2003', family: 'Windows Server', label: 'Windows Server 2003 / 2003 R2', match: /windows server 2003/, eos: '2015-07-14', source: 'Microsoft Lifecycle: extended support ended 14 Jul 2015 (2003 and 2003 R2 share the date).' },
  { id: 'ws2008r2', family: 'Windows Server', label: 'Windows Server 2008 R2', match: /windows server 2008 r2/, eos: '2020-01-14', source: 'Microsoft Lifecycle: extended support ended 14 Jan 2020. Paid ESU and Azure-hosted extensions ran to Jan 2023 — chargeable, per server, and never assumed here.' },
  { id: 'ws2008', family: 'Windows Server', label: 'Windows Server 2008', match: /windows server 2008/, eos: '2020-01-14', source: 'Microsoft Lifecycle: extended support ended 14 Jan 2020.' },
  { id: 'ws2012r2', family: 'Windows Server', label: 'Windows Server 2012 R2', match: /windows server 2012 r2/, eos: '2023-10-10', source: 'Microsoft Lifecycle: extended support ended 10 Oct 2023. ESU available to Oct 2026 at additional cost.' },
  { id: 'ws2012', family: 'Windows Server', label: 'Windows Server 2012', match: /windows server 2012/, eos: '2023-10-10', source: 'Microsoft Lifecycle: extended support ended 10 Oct 2023. ESU available to Oct 2026 at additional cost.' },
  { id: 'ws2016', family: 'Windows Server', label: 'Windows Server 2016', match: /windows server 2016/, eos: '2027-01-12', source: 'Microsoft Lifecycle: extended support ends 12 Jan 2027.' },
  { id: 'ws2019', family: 'Windows Server', label: 'Windows Server 2019', match: /windows server 2019/, eos: '2029-01-09', source: 'Microsoft Lifecycle: extended support ends 9 Jan 2029.' },
  { id: 'ws2022', family: 'Windows Server', label: 'Windows Server 2022', match: /windows server 2022/, eos: '2031-10-14', source: 'Microsoft Lifecycle: extended support ends 14 Oct 2031.' },
  { id: 'ws2025', family: 'Windows Server', label: 'Windows Server 2025', match: /windows server 2025/, eos: '2034-10-10', source: 'Microsoft Lifecycle: extended support ends 10 Oct 2034.' },

  // ---- Windows client ----
  { id: 'winxp', family: 'Windows client', label: 'Windows XP', match: /windows xp/, eos: '2014-04-08', source: 'Microsoft Lifecycle: extended support ended 8 Apr 2014.' },
  { id: 'winvista', family: 'Windows client', label: 'Windows Vista', match: /windows vista/, eos: '2017-04-11', source: 'Microsoft Lifecycle: extended support ended 11 Apr 2017.' },
  { id: 'win7', family: 'Windows client', label: 'Windows 7', match: /windows 7/, eos: '2020-01-14', source: 'Microsoft Lifecycle: extended support ended 14 Jan 2020 (ESU to Jan 2023, chargeable).' },
  { id: 'win81', family: 'Windows client', label: 'Windows 8.1', match: /windows 8\.1/, eos: '2023-01-10', source: 'Microsoft Lifecycle: extended support ended 10 Jan 2023.' },
  { id: 'win8', family: 'Windows client', label: 'Windows 8', match: /windows 8(?!\.)/, eos: '2016-01-12', source: 'Microsoft Lifecycle: support ended 12 Jan 2016 — the 8.1 update was mandatory to stay supported.' },
  { id: 'win10', family: 'Windows client', label: 'Windows 10', match: /windows 10/, eos: '2025-10-14', source: 'Microsoft Lifecycle: end of support 14 Oct 2025 for all editions EXCEPT the LTSC SKUs, which run to 2027/2032 and are NOT distinguishable from this OS string. Verify LTSC estates separately.' },
  { id: 'win11', family: 'Windows client', label: 'Windows 11 (feature update not stated)', match: /windows 11/, eos: null, ambiguous: true, source: 'Windows 11 has NO family-level end of support. Each annual feature update (22H2, 23H2, 24H2, 25H2) carries its own date 24-36 months apart, so a bare "Windows 11" string cannot be dated. Capture the feature update to date these.' },

  // ---- Red Hat Enterprise Linux ----
  { id: 'rhel10', family: 'Red Hat Enterprise Linux', label: 'RHEL 10', match: /(?:red hat enterprise linux|rhel) ?10/, eos: '2035-05-31', source: 'Red Hat Enterprise Linux Life Cycle: full + maintenance phases run to May 2035.' },
  { id: 'rhel9', family: 'Red Hat Enterprise Linux', label: 'RHEL 9', match: /(?:red hat enterprise linux|rhel) ?9/, eos: '2032-05-31', source: 'Red Hat Enterprise Linux Life Cycle: maintenance support ends 31 May 2032.' },
  { id: 'rhel8', family: 'Red Hat Enterprise Linux', label: 'RHEL 8', match: /(?:red hat enterprise linux|rhel) ?8/, eos: '2029-05-31', source: 'Red Hat Enterprise Linux Life Cycle: maintenance support ends 31 May 2029.' },
  { id: 'rhel7', family: 'Red Hat Enterprise Linux', label: 'RHEL 7', match: /(?:red hat enterprise linux|rhel) ?7/, eos: '2024-06-30', source: 'Red Hat Enterprise Linux Life Cycle: maintenance support ended 30 Jun 2024. ELS is a paid add-on and is never assumed here.' },
  { id: 'rhel6', family: 'Red Hat Enterprise Linux', label: 'RHEL 6', match: /(?:red hat enterprise linux|rhel) ?6/, eos: '2020-11-30', source: 'Red Hat Enterprise Linux Life Cycle: maintenance support ended 30 Nov 2020.' },
  { id: 'rhel5', family: 'Red Hat Enterprise Linux', label: 'RHEL 5', match: /(?:red hat enterprise linux|rhel) ?5/, eos: '2017-03-31', source: 'Red Hat Enterprise Linux Life Cycle: maintenance support ended 31 Mar 2017.' },
  { id: 'rhel4', family: 'Red Hat Enterprise Linux', label: 'RHEL 4', match: /(?:red hat enterprise linux|rhel) ?4/, eos: '2012-02-29', source: 'Red Hat Enterprise Linux Life Cycle: ended 29 Feb 2012.' },
  { id: 'rhelx', family: 'Red Hat Enterprise Linux', label: 'RHEL (release not stated)', match: /red hat|rhel/, eos: null, ambiguous: true, source: 'No major release number in the OS string, so no lifecycle row applies.' },

  // ---- CentOS ----
  { id: 'centos9', family: 'CentOS', label: 'CentOS Stream 9', match: /centos ?(?:stream )?9/, eos: '2027-05-31', source: 'CentOS Stream 9 tracks the RHEL 9 full-support phase, ending May 2027.' },
  { id: 'centos8', family: 'CentOS', label: 'CentOS 8 / Stream 8', match: /centos ?(?:stream )?8/, eos: '2021-12-31', source: 'CentOS Project: CentOS Linux 8 was discontinued 31 Dec 2021 (Stream 8 ended May 2024). Verify which of the two this is.' },
  { id: 'centos7', family: 'CentOS', label: 'CentOS 7', match: /centos ?7/, eos: '2024-06-30', source: 'CentOS Project: end of life 30 Jun 2024.' },
  { id: 'centos6', family: 'CentOS', label: 'CentOS 6', match: /centos ?6/, eos: '2020-11-30', source: 'CentOS Project: end of life 30 Nov 2020.' },
  { id: 'centos5', family: 'CentOS', label: 'CentOS 5', match: /centos ?5/, eos: '2017-03-31', source: 'CentOS Project: end of life 31 Mar 2017.' },
  { id: 'centosx', family: 'CentOS', label: 'CentOS (release not stated)', match: /centos/, eos: null, ambiguous: true, source: 'No single major release in the OS string.' },

  // ---- Oracle Linux ----
  { id: 'ol9', family: 'Oracle Linux', label: 'Oracle Linux 9', match: /oracle linux ?9/, eos: '2032-06-30', source: 'Oracle Linux Lifetime Support: premier support to Jun 2032.' },
  { id: 'ol8', family: 'Oracle Linux', label: 'Oracle Linux 8', match: /oracle linux ?8/, eos: '2029-07-31', source: 'Oracle Linux Lifetime Support: premier support to Jul 2029.' },
  { id: 'ol7', family: 'Oracle Linux', label: 'Oracle Linux 7', match: /oracle linux ?7/, eos: '2024-12-31', source: 'Oracle Linux Lifetime Support: premier support ended Dec 2024.' },
  { id: 'ol6', family: 'Oracle Linux', label: 'Oracle Linux 6', match: /oracle linux ?6/, eos: '2021-03-31', source: 'Oracle Linux Lifetime Support: premier support ended Mar 2021.' },
  { id: 'olx', family: 'Oracle Linux', label: 'Oracle Linux (release not stated)', match: /oracle linux/, eos: null, ambiguous: true, source: 'No single major release in the OS string.' },

  // ---- Ubuntu (LTS only — interim releases are deliberately not carried) ----
  { id: 'ubuntu2404', family: 'Ubuntu', label: 'Ubuntu 24.04 LTS', match: /ubuntu[^0-9]*24\.04/, eos: '2029-05-31', source: 'Canonical: 5-year standard LTS support to May 2029. Ubuntu Pro / ESM extends this for a fee and is not assumed.' },
  { id: 'ubuntu2204', family: 'Ubuntu', label: 'Ubuntu 22.04 LTS', match: /ubuntu[^0-9]*22\.04/, eos: '2027-06-01', source: 'Canonical: 5-year standard LTS support to Jun 2027.' },
  { id: 'ubuntu2004', family: 'Ubuntu', label: 'Ubuntu 20.04 LTS', match: /ubuntu[^0-9]*20\.04/, eos: '2025-05-31', source: 'Canonical: 5-year standard LTS support ended May 2025.' },
  { id: 'ubuntu1804', family: 'Ubuntu', label: 'Ubuntu 18.04 LTS', match: /ubuntu[^0-9]*18\.04/, eos: '2023-05-31', source: 'Canonical: 5-year standard LTS support ended May 2023.' },
  { id: 'ubuntu1604', family: 'Ubuntu', label: 'Ubuntu 16.04 LTS', match: /ubuntu[^0-9]*16\.04/, eos: '2021-04-30', source: 'Canonical: 5-year standard LTS support ended Apr 2021.' },
  { id: 'ubuntu1404', family: 'Ubuntu', label: 'Ubuntu 14.04 LTS', match: /ubuntu[^0-9]*14\.04/, eos: '2019-04-30', source: 'Canonical: 5-year standard LTS support ended Apr 2019.' },
  { id: 'ubuntux', family: 'Ubuntu', label: 'Ubuntu (release not stated)', match: /ubuntu/, eos: null, ambiguous: true, source: 'The OS string carries no release. "Ubuntu Linux" spans supported LTS releases and releases that died years ago — a nine-year spread. Capture the release (e.g. 20.04) to date these.' },

  // ---- Debian ----
  { id: 'debian13', family: 'Debian', label: 'Debian 13 (trixie)', match: /debian[^0-9]*13/, eos: '2030-06-30', source: 'Debian LTS: projected 5-year LTS window from the 2025 release. Projection, not a published end date — verify.' },
  { id: 'debian12', family: 'Debian', label: 'Debian 12 (bookworm)', match: /debian[^0-9]*12/, eos: '2028-06-30', source: 'Debian LTS: projected LTS end Jun 2028.' },
  { id: 'debian11', family: 'Debian', label: 'Debian 11 (bullseye)', match: /debian[^0-9]*11/, eos: '2026-08-31', source: 'Debian LTS: LTS ends Aug 2026.' },
  { id: 'debian10', family: 'Debian', label: 'Debian 10 (buster)', match: /debian[^0-9]*10/, eos: '2024-06-30', source: 'Debian LTS: LTS ended Jun 2024.' },
  { id: 'debian9', family: 'Debian', label: 'Debian 9 (stretch)', match: /debian[^0-9]*9/, eos: '2022-06-30', source: 'Debian LTS: LTS ended Jun 2022.' },
  { id: 'debian8', family: 'Debian', label: 'Debian 8 (jessie)', match: /debian[^0-9]*8/, eos: '2020-06-30', source: 'Debian LTS: LTS ended Jun 2020.' },
  { id: 'debian7', family: 'Debian', label: 'Debian 7 (wheezy)', match: /debian[^0-9]*7/, eos: '2018-05-31', source: 'Debian LTS: LTS ended May 2018.' },
  { id: 'debian6', family: 'Debian', label: 'Debian 6 (squeeze)', match: /debian[^0-9]*6/, eos: '2016-02-29', source: 'Debian LTS: LTS ended Feb 2016.' },
  { id: 'debian5', family: 'Debian', label: 'Debian 5 (lenny)', match: /debian[^0-9]*5/, eos: '2012-02-06', source: 'Debian: security support ended Feb 2012.' },
  { id: 'debianx', family: 'Debian', label: 'Debian (release not stated)', match: /debian/, eos: null, ambiguous: true, source: 'No release number in the OS string.' },

  // ---- SUSE ----
  { id: 'sles15', family: 'SUSE Linux Enterprise', label: 'SLES 15', match: /(?:suse linux enterprise[a-z ]*|sles ?)15/, eos: '2031-07-31', source: 'SUSE Product Support Lifecycle: general support to Jul 2031.' },
  { id: 'sles12', family: 'SUSE Linux Enterprise', label: 'SLES 12', match: /(?:suse linux enterprise[a-z ]*|sles ?)12/, eos: '2027-10-31', source: 'SUSE Product Support Lifecycle: LTSS window to Oct 2027. General support ended Oct 2024 — LTSS is chargeable.' },
  { id: 'sles11', family: 'SUSE Linux Enterprise', label: 'SLES 11', match: /(?:suse linux enterprise[a-z ]*|sles ?)11/, eos: '2019-03-31', source: 'SUSE Product Support Lifecycle: general support ended Mar 2019.' },
  { id: 'sles10', family: 'SUSE Linux Enterprise', label: 'SLES 10', match: /(?:suse linux enterprise[a-z ]*|sles ?)10/, eos: '2013-07-31', source: 'SUSE Product Support Lifecycle: general support ended Jul 2013.' },
  { id: 'slesx', family: 'SUSE Linux Enterprise', label: 'SUSE Linux Enterprise (release not stated)', match: /suse|sles/, eos: null, ambiguous: true, source: 'No release number in the OS string.' },

  // ---- Oracle Solaris ----
  { id: 'sol11', family: 'Oracle Solaris', label: 'Solaris 11', match: /solaris ?11/, eos: '2034-11-30', source: 'Oracle Lifetime Support: premier support to Nov 2034.' },
  { id: 'sol10', family: 'Oracle Solaris', label: 'Solaris 10', match: /solaris ?10/, eos: '2024-01-31', source: 'Oracle Lifetime Support: extended support ended Jan 2024.' },
  { id: 'sol9', family: 'Oracle Solaris', label: 'Solaris 9', match: /solaris ?9/, eos: '2014-10-31', source: 'Oracle Lifetime Support: extended support ended Oct 2014.' },
  { id: 'solx', family: 'Oracle Solaris', label: 'Solaris (release not stated)', match: /solaris/, eos: null, ambiguous: true, source: 'No release number in the OS string.' },

  // ---- FreeBSD ----
  { id: 'fbsd14', family: 'FreeBSD', label: 'FreeBSD 14', match: /freebsd ?14/, eos: '2028-11-30', source: 'FreeBSD Security Team: projected end of life for the 14.x branch. Projection — verify.' },
  { id: 'fbsd13', family: 'FreeBSD', label: 'FreeBSD 13', match: /freebsd ?13/, eos: '2026-01-31', source: 'FreeBSD Security Team: 13.x branch end of life Jan 2026.' },
  { id: 'fbsd12', family: 'FreeBSD', label: 'FreeBSD 12', match: /freebsd ?12/, eos: '2023-12-31', source: 'FreeBSD Security Team: 12.x branch end of life Dec 2023.' },
  { id: 'fbsdx', family: 'FreeBSD', label: 'FreeBSD (release not stated)', match: /freebsd/, eos: null, ambiguous: true, source: 'No release number in the OS string.' },

  // ---- VMware ----
  { id: 'esxi8', family: 'VMware ESXi', label: 'ESXi 8.0', match: /esxi ?8/, eos: '2027-10-11', source: 'Broadcom/VMware lifecycle: general support for vSphere 8 ends Oct 2027.' },
  { id: 'esxi7', family: 'VMware ESXi', label: 'ESXi 7.0', match: /esxi ?7/, eos: '2025-04-02', source: 'Broadcom/VMware lifecycle: general support for vSphere 7 ended Apr 2025.' },
  { id: 'esxi6', family: 'VMware ESXi', label: 'ESXi 6.5 / 6.7', match: /esxi ?6/, eos: '2022-10-15', source: 'Broadcom/VMware lifecycle: general support ended Oct 2022.' },
  { id: 'esxix', family: 'VMware ESXi', label: 'VMware ESXi (release not stated)', match: /esxi|vsphere/, eos: null, ambiguous: true, source: 'No release number in the OS string.' },
  { id: 'photon', family: 'VMware Photon OS', label: 'VMware Photon OS / Photon CRX', match: /photon/, eos: null, ambiguous: true, source: 'Photon is an appliance-embedded guest. Its support follows the appliance product (vCenter, NSX, Aria) rather than the guest OS, so it cannot be dated from this field. Assess the appliance instead.' },

  // ---- last-resort family buckets ----
  { id: 'genericlinux', family: 'Linux (unidentified)', label: 'Linux — distribution not identified', match: /linux/, eos: null, ambiguous: true, source: 'The string names no distribution and release this table can date.' },
  { id: 'genericwin', family: 'Windows (unidentified)', label: 'Windows — release not identified', match: /windows/, eos: null, ambiguous: true, source: 'The string names no Windows release this table can date.' },
];

// Guards applied BEFORE the table. Each one describes a shape of OS string that
// is structurally undatable: a floor rather than a release, a list of possible
// releases, a wildcard, a placeholder. These are the strings a lazier report
// would quietly bucket as "supported".
export const VAGUE_RULES = [
  { id: 'orlater', test: /\b(?:or|and) (?:later|newer|above|higher)\b/, why: 'the string says "or later" — it names a floor, not a release' },
  { id: 'multi', test: /\d\s*\/\s*\d/, why: 'the string lists several possible releases (e.g. "4/5/6/7")' },
  { id: 'other', test: /^other\b/, why: 'a generic "Other" guest-OS descriptor — it names nothing' },
  { id: 'wildcard', test: /\b\d+(?:\.\d+)*\.x\b/, why: 'the string uses an ".x" wildcard instead of a release' },
  { id: 'unknown', test: /\bunknown\b|\bnot specified\b|\bn\/a\b/, why: 'the string itself records the OS as unknown' },
];

// ---------------------------------------------------------------------------
// Value access — the same merged accessor Rightsizing.js uses. Fact sheets
// reach the UI in two shapes depending on the loader that wrote them: the
// Postgres/DynamoDB rows carry a `data` jsonb object, the CSV/XLSX import path
// writes `fields`. Read BOTH, let `data` win, fabricate nothing.
// ---------------------------------------------------------------------------
export function specsOf(fs) {
  return { ...(fs.fields || {}), ...(fs.data || {}) };
}

const text = (v) => String(v === undefined || v === null ? '' : v).trim();
const fmt = (n) => (n === null || n === undefined ? DASH : Math.round(n).toLocaleString());
const pct = (n) => (n === null || n === undefined ? DASH : `${Number(n).toFixed(1)}%`);

function isServerRecord(d) {
  if (/^server$/i.test(text(d.category))) return true;
  if (text(d.os)) return true;
  return d.vcpu !== undefined || d.ramGb !== undefined || d.storageGb !== undefined;
}

// Normalise an OS string down to something the patterns can match without
// losing the release information that is the whole point.
export function normalizeOs(raw) {
  return text(raw)
    .toLowerCase()
    .replace(/\((?:32|64)[\s-]?bit\)/g, ' ')
    .replace(/\b(?:32|64)[\s-]?bit\b/g, ' ')
    .replace(/\bmicrosoft\b/g, ' ')
    .replace(/[®™,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Resolve an OS string against the reference table.
// Returns { entry, eos, ambiguous, why } — `eos` is null whenever the string is
// too vague to date, and `why` always explains which rule decided that.
export function lookupEol(rawOs) {
  const norm = normalizeOs(rawOs);
  if (!norm) {
    return { entry: null, family: 'OS not recorded', eos: null, ambiguous: true, why: 'No operating system is recorded on this fact sheet.' };
  }

  const firstMatch = () => {
    for (let i = 0; i < EOL_REFERENCE.length; i += 1) {
      if (EOL_REFERENCE[i].match.test(norm)) return EOL_REFERENCE[i];
    }
    return null;
  };

  // Structural guards run BEFORE any date is read. A string caught here is
  // undatable by construction. It is still attributed to an OS family where the
  // string names one — "Windows Server 2016 or later" plainly says Windows
  // Server — because grouping by the family the string itself states is
  // reading, not inferring. The DATE stays null regardless of what the family
  // entry happens to carry; that is the whole distinction this file defends.
  for (let i = 0; i < VAGUE_RULES.length; i += 1) {
    if (VAGUE_RULES[i].test.test(norm)) {
      const hint = firstMatch();
      return {
        entry: null,
        family: hint ? hint.family : 'Unrecognised',
        eos: null,
        ambiguous: true,
        vagueRule: VAGUE_RULES[i].id,
        why: `Too vague to date — ${VAGUE_RULES[i].why}. No date is guessed${hint ? `, even though the string names the ${hint.family} family` : ''}.`,
      };
    }
  }

  const e = firstMatch();
  if (e) {
    if (e.ambiguous || !e.eos) return { entry: e, family: e.family, eos: null, ambiguous: true, why: `Too vague to date — ${e.source}` };
    return { entry: e, family: e.family, eos: e.eos, ambiguous: false, why: e.source };
  }
  return {
    entry: null,
    family: 'Unrecognised',
    eos: null,
    ambiguous: true,
    why: 'No entry in Primordia\'s reference table matches this OS string. Add one, or correct the record.',
  };
}

// Vendor-supplied end-of-support date. Several field names are accepted because
// different import paths spell it differently; the name that actually carried
// the value is recorded so a row can always be traced back to its field.
const VENDOR_EOS_KEYS = ['osEos', 'osEndOfSupport', 'osEol', 'osEndOfLife', 'endOfSupport', 'eosDate', 'supportEndDate'];

function parseDate(v) {
  const s = text(v);
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t) : null;
}

const isoOf = (d) => (d ? d.toISOString().slice(0, 10) : '');

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------
export const CLASSES = {
  EOS: 'End of support',
  Y1: 'Ending within 12 months',
  Y2: 'Ending within 24 months',
  OK: 'Supported',
  UNKNOWN: 'Unknown — cannot be assessed',
};

const CLASS_ORDER = [CLASSES.EOS, CLASSES.Y1, CLASSES.Y2, CLASSES.OK, CLASSES.UNKNOWN];

const CLASS_COLOR = {
  [CLASSES.EOS]: RED,
  [CLASSES.Y1]: AMBER,
  [CLASSES.Y2]: '#B79100',
  [CLASSES.OK]: GREEN,
  [CLASSES.UNKNOWN]: PURPLE,
};

// Risk weight per class. Unknown carries NO weight on purpose — it is not a
// small risk, it is an unmeasured one, and mixing the two would let a workspace
// with no data score better than one that captured its estate honestly.
const RISK_WEIGHT = {
  [CLASSES.EOS]: 1,
  [CLASSES.Y1]: 0.6,
  [CLASSES.Y2]: 0.3,
  [CLASSES.OK]: 0,
};

const CLASS_NOTE = {
  [CLASSES.EOS]: 'Support date already passed — no vendor security patches without a paid extension',
  [CLASSES.Y1]: 'Support ends inside 12 months — remediation must already be funded',
  [CLASSES.Y2]: 'Support ends inside 24 months — belongs in next year\'s plan',
  [CLASSES.OK]: 'Support date is more than 24 months out',
  [CLASSES.UNKNOWN]: 'No usable date — this is missing data, NOT a supported server',
};

function classify(eosDate, now) {
  if (!eosDate) return CLASSES.UNKNOWN;
  const days = Math.floor((eosDate.getTime() - now.getTime()) / DAY_MS);
  if (days < 0) return CLASSES.EOS;
  if (days <= 365) return CLASSES.Y1;
  if (days <= 730) return CLASSES.Y2;
  return CLASSES.OK;
}

// ---------------------------------------------------------------------------
// Mitigation. Only evidence recorded ON the record (or on a deployment it
// hosts) counts. "Not Scheduled" is explicitly NOT a plan.
// ---------------------------------------------------------------------------
const MITIGATION_SIGNALS = [
  'a migration wave other than "Not Scheduled"',
  'a planned cutover date',
  'a planned decommission date',
  'a lifecycle state of Phase Out / End of Life / Decommissioned',
  'a recorded replacement, target OS or remediation plan',
];

function mitigationOf(d, deploymentSignals) {
  const notes = [];
  const wave = text(d.migrationWave);
  if (wave && !/^not scheduled$/i.test(wave)) notes.push(`migration wave "${wave}"`);
  if (parseDate(d.cutoverDate)) notes.push(`cutover ${isoOf(parseDate(d.cutoverDate))}`);
  if (parseDate(d.decommissionDate)) notes.push(`decommission ${isoOf(parseDate(d.decommissionDate))}`);
  const ls = text(d.lifecycleState);
  if (/^(phase out|end of life|decommissioned)$/i.test(ls)) notes.push(`lifecycle state "${ls}"`);
  ['replacedBy', 'targetOs', 'plannedOs', 'remediationPlan', 'replacementPlan', 'upgradePlan']
    .forEach((k) => { if (text(d[k])) notes.push(`${k}: ${text(d[k])}`); });
  deploymentSignals.forEach((s) => notes.push(s));
  return { mitigated: notes.length > 0, note: notes.join('; ') };
}

// ---------------------------------------------------------------------------
// Application linkage. Server -> Deployment -> Application, plus the direct
// Application -> IT Component relation. Both directions of each relation are
// read because the model records them from either end.
// ---------------------------------------------------------------------------
export function buildLinkage(servers, deployments, applications) {
  const appById = new Map();
  applications.forEach((a) => {
    const d = specsOf(a);
    appById.set(a.id, {
      id: a.id,
      name: a.name,
      criticality: text(d.businessCriticality),
    });
  });

  const depById = new Map();
  deployments.forEach((dep) => {
    const d = specsOf(dep);
    const appRel = (dep.relations || {}).application || [];
    depById.set(dep.id, {
      id: dep.id,
      name: dep.name,
      environment: text(d.environment) || text(d.environmentLabel),
      appId: appRel.length ? appRel[0].id : null,
      migrationWave: text(d.migrationWave),
      cutoverDate: d.cutoverDate,
      decommissionDate: d.decommissionDate,
    });
  });

  // serverId -> Set(deploymentId)
  const depsOfServer = new Map();
  const addDep = (sid, did) => {
    if (!sid || !did) return;
    if (!depsOfServer.has(sid)) depsOfServer.set(sid, new Set());
    depsOfServer.get(sid).add(did);
  };
  servers.forEach((s) => {
    ((s.relations || {}).hostedDeployments || []).forEach((r) => addDep(s.id, r.id));
  });
  deployments.forEach((dep) => {
    ((dep.relations || {}).servers || []).forEach((r) => addDep(r.id, dep.id));
  });

  // serverId -> Set(appId), including the direct Application -> IT Component link
  const appsOfServer = new Map();
  const addApp = (sid, aid) => {
    if (!sid || !aid) return;
    if (!appsOfServer.has(sid)) appsOfServer.set(sid, new Set());
    appsOfServer.get(sid).add(aid);
  };
  applications.forEach((a) => {
    ((a.relations || {}).itComponents || []).forEach((r) => addApp(r.id, a.id));
  });
  depsOfServer.forEach((set, sid) => {
    set.forEach((did) => {
      const dep = depById.get(did);
      if (dep && dep.appId) addApp(sid, dep.appId);
    });
  });

  return { appById, depById, depsOfServer, appsOfServer };
}

const CRIT_RANK = (v) => {
  const i = PICKLISTS.criticality.indexOf(v);
  return i < 0 ? -1 : i;
};
const MISSION_CRITICAL = new Set(['Mission Critical', 'Business Critical']);

// ---------------------------------------------------------------------------
// The single analysis pass. One traversal of the server list produces every
// row, every count and every breakdown on the screen — 7,000+ records must not
// be walked once per widget.
// ---------------------------------------------------------------------------
export function analyzeObsolescence(servers, linkage, now) {
  const { appById, depById, depsOfServer, appsOfServer } = linkage;

  const counts = {};
  CLASS_ORDER.forEach((c) => { counts[c] = 0; });

  const dateSource = { vendor: 0, inferred: 0, none: 0, unparseable: 0 };
  const entryHits = new Map();     // reference entry id -> servers matched
  const vagueHits = new Map();     // reason string -> count (for the missing-data breakdown)
  const byFamily = new Map();
  const byCluster = new Map();
  const byDatacenter = new Map();
  const byApp = new Map();
  let unlinked = 0;                // at-risk servers with no application link at all
  let mitigationFieldsSeen = 0;    // records carrying ANY mitigation field, at risk or not

  const bump = (map, key, row) => {
    if (!map.has(key)) {
      const blank = { key, total: 0, risk: 0 };
      CLASS_ORDER.forEach((c) => { blank[c] = 0; });
      map.set(key, blank);
    }
    const g = map.get(key);
    g.total += 1;
    g[row.cls] += 1;
    g.risk += RISK_WEIGHT[row.cls] || 0;
    return g;
  };

  const rows = servers.map((fs) => {
    const d = specsOf(fs);
    const osRaw = text(d.os);

    // --- Date resolution. Vendor first, ALWAYS. -----------------------------
    let vendorKey = '';
    let vendorRaw = '';
    for (let i = 0; i < VENDOR_EOS_KEYS.length; i += 1) {
      if (text(d[VENDOR_EOS_KEYS[i]])) { vendorKey = VENDOR_EOS_KEYS[i]; vendorRaw = text(d[VENDOR_EOS_KEYS[i]]); break; }
    }
    const vendorDate = parseDate(vendorRaw);
    const ref = lookupEol(osRaw);
    const inferredDate = ref.eos ? parseDate(ref.eos) : null;

    let eosDate = null;
    let source = 'none';
    let sourceLabel = 'no date available';
    let evidence = '';

    if (vendorDate) {
      eosDate = vendorDate;
      source = 'vendor';
      sourceLabel = 'vendor date';
      evidence = `Vendor-supplied end-of-support date read from the record's "${vendorKey}" field. This takes precedence over Primordia's reference table${inferredDate ? ` (which would have inferred ${ref.eos} from the OS name).` : '.'}`;
    } else if (vendorRaw) {
      dateSource.unparseable += 1;
      if (inferredDate) {
        eosDate = inferredDate;
        source = 'inferred';
        sourceLabel = 'inferred from OS name';
        evidence = `The record's "${vendorKey}" field holds "${vendorRaw}", which is not a readable date, so it was NOT used. Date inferred from the OS name via reference entry "${ref.entry ? ref.entry.label : ''}". ${ref.why}`;
      } else {
        evidence = `The record's "${vendorKey}" field holds "${vendorRaw}", which is not a readable date, and the OS string cannot be dated either. ${ref.why}`;
      }
    } else if (inferredDate) {
      eosDate = inferredDate;
      source = 'inferred';
      sourceLabel = 'inferred from OS name';
      evidence = `No vendor end-of-support date on this record. Date inferred from the OS name via Primordia reference entry "${ref.entry ? ref.entry.label : ''}" — ${ref.why}`;
    } else {
      evidence = `No vendor end-of-support date on this record, and the OS string cannot be dated. ${ref.why}`;
    }

    if (source === 'vendor') dateSource.vendor += 1;
    else if (source === 'inferred') dateSource.inferred += 1;
    else dateSource.none += 1;

    const cls = classify(eosDate, now);
    counts[cls] += 1;

    const days = eosDate ? Math.floor((eosDate.getTime() - now.getTime()) / DAY_MS) : null;

    // --- Reference-table usage and missing-data reasons --------------------
    if (ref.entry) entryHits.set(ref.entry.id, (entryHits.get(ref.entry.id) || 0) + 1);
    if (cls === CLASSES.UNKNOWN) {
      const reason = ref.why;
      vagueHits.set(reason, (vagueHits.get(reason) || 0) + 1);
    }

    // --- Mitigation --------------------------------------------------------
    const depSignals = [];
    const depIds = depsOfServer.get(fs.id);
    if (depIds) {
      depIds.forEach((did) => {
        const dep = depById.get(did);
        if (!dep) return;
        if (dep.migrationWave && !/^not scheduled$/i.test(dep.migrationWave)) depSignals.push(`deployment "${dep.name}" in ${dep.migrationWave}`);
        if (parseDate(dep.cutoverDate)) depSignals.push(`deployment "${dep.name}" cutover ${isoOf(parseDate(dep.cutoverDate))}`);
        if (parseDate(dep.decommissionDate)) depSignals.push(`deployment "${dep.name}" decommission ${isoOf(parseDate(dep.decommissionDate))}`);
      });
    }
    const mit = mitigationOf(d, depSignals);
    if (mit.mitigated) mitigationFieldsSeen += 1;

    // --- Application linkage ----------------------------------------------
    const appIds = appsOfServer.get(fs.id);
    const apps = [];
    let topCrit = '';
    if (appIds) {
      appIds.forEach((aid) => {
        const a = appById.get(aid);
        if (!a) return;
        apps.push(a);
        if (CRIT_RANK(a.criticality) > CRIT_RANK(topCrit)) topCrit = a.criticality;
      });
    }

    const row = {
      id: fs.id,
      name: fs.name,
      os: osRaw,
      family: ref.family || 'Unrecognised',
      refLabel: ref.entry ? ref.entry.label : '',
      refId: ref.entry ? ref.entry.id : '',
      vendorKey,
      vendorRaw,
      osSupport: text(d.osSupport),
      eosIso: isoOf(eosDate),
      source,
      sourceLabel,
      evidence,
      cls,
      days,
      cluster: text(d.cluster),
      datacenter: text(d.datacenter),
      host: text(d.host),
      powerState: text(d.powerState),
      sourceSystem: text(d.sourceSystem),
      apps,
      appNames: apps.map((a) => a.name).join(' · '),
      criticality: topCrit,
      mitigated: mit.mitigated,
      mitigationNote: mit.note,
    };

    bump(byFamily, row.family, row);
    bump(byCluster, row.cluster || '(cluster not recorded)', row);
    bump(byDatacenter, row.datacenter || '(datacenter not recorded)', row);
    if (apps.length) {
      apps.forEach((a) => {
        const g = bump(byApp, a.name, row);
        g.criticality = a.criticality;
      });
    } else if (RISK_WEIGHT[row.cls] > 0 || row.cls === CLASSES.UNKNOWN) {
      unlinked += 1;
    }

    return row;
  });

  // --- The four scores ------------------------------------------------------
  const total = rows.length;
  const assessable = total - counts[CLASSES.UNKNOWN];
  const atRiskRows = rows.filter((r) => (RISK_WEIGHT[r.cls] || 0) > 0);

  let aggregatedRisk = 0;
  let mitigatedRisk = 0;
  atRiskRows.forEach((r) => {
    const w = RISK_WEIGHT[r.cls] || 0;
    aggregatedRisk += w;
    if (r.mitigated) mitigatedRisk += w;
  });

  const scores = {
    total,
    assessable,
    atRisk: atRiskRows.length,
    aggregatedRisk,
    // Aggregated risk as a share of the worst case the ASSESSABLE set could
    // carry (every assessable server past end of support).
    aggregatedRiskPct: assessable ? (aggregatedRisk / assessable) * 100 : 0,
    mitigatedRisk,
    mitigatedPct: aggregatedRisk ? (mitigatedRisk / aggregatedRisk) * 100 : 0,
    unaddressedRisk: aggregatedRisk - mitigatedRisk,
    unaddressedPct: aggregatedRisk ? ((aggregatedRisk - mitigatedRisk) / aggregatedRisk) * 100 : 0,
    missingDataPct: total ? (counts[CLASSES.UNKNOWN] / total) * 100 : 0,
    missingData: counts[CLASSES.UNKNOWN],
    mitigationFieldsSeen,
    unlinked,
  };

  const sortGroups = (map) => [...map.values()].sort((a, b) => (b.risk - a.risk) || (b.total - a.total));

  return {
    rows,
    counts,
    scores,
    dateSource,
    entryHits,
    vagueReasons: [...vagueHits.entries()].map(([why, n]) => ({ why, n })).sort((a, b) => b.n - a.n),
    byFamily: sortGroups(byFamily),
    byCluster: sortGroups(byCluster),
    byDatacenter: sortGroups(byDatacenter),
    byApp: [...byApp.values()].sort(
      (a, b) => (CRIT_RANK(b.criticality) - CRIT_RANK(a.criticality)) || (b.risk - a.risk) || (b.total - a.total)
    ),
  };
}

// ---------------------------------------------------------------------------
// CSV — the caveat travels with the file
// ---------------------------------------------------------------------------
function csvCell(v) {
  if (v === undefined || v === null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsvWithBanner(filename, bannerLines, columns, rows) {
  const banner = bannerLines.map((l) => `# ${l}`).join('\n');
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(c.value(r))).join(',')).join('\n');
  const blob = new Blob([`${banner}\n\n${header}\n${body}\n`], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
function ScoreCard({ label, value, definition, accent, footnote }) {
  return (
    <Paper sx={{ p: 1.5, borderTop: `3px solid ${accent}`, display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ fontSize: 30, fontWeight: 700, color: NAVY, lineHeight: 1.15 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11.5, color: GREY, mt: 0.5, flexGrow: 1 }}>{definition}</Typography>
      {footnote ? (
        <Typography sx={{ fontSize: 11, color: AMBER, fontWeight: 700, mt: 0.5 }}>{footnote}</Typography>
      ) : null}
    </Paper>
  );
}

function ClassChip({ cls, small }) {
  return (
    <Chip
      size="small"
      label={cls}
      sx={{
        height: small ? 18 : 20,
        fontWeight: 700,
        fontSize: small ? 10 : 11,
        bgcolor: 'transparent',
        border: `1px solid ${CLASS_COLOR[cls]}`,
        color: CLASS_COLOR[cls],
      }}
    />
  );
}

function SourceChip({ source, label }) {
  const color = source === 'vendor' ? GREEN : source === 'inferred' ? BLUE : GREY;
  return (
    <Chip
      size="small"
      icon={source === 'vendor' ? <VerifiedIcon sx={{ fontSize: 13 }} /> : undefined}
      label={label}
      sx={{ height: 19, fontSize: 10.5, fontWeight: 700, bgcolor: 'transparent', border: `1px solid ${color}`, color }}
    />
  );
}

// A breakdown table shared by the OS-family / cluster / datacenter / app tabs.
function GroupTable({ groups, keyLabel, showCriticality, total }) {
  const shown = groups.slice(0, MAX_GROUP_ROWS);
  return (
    <TableContainer component={Paper}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>{keyLabel}</TableCell>
            {showCriticality && <TableCell sx={{ fontWeight: 700 }}>Business criticality</TableCell>}
            <TableCell align="right" sx={{ fontWeight: 700 }}>Servers</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: RED }}>End of support</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: AMBER }}>≤ 12 mo</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>≤ 24 mo</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: GREEN }}>Supported</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: PURPLE }}>Unknown</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Missing data %</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Aggregated risk</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((g) => {
            const crit = MISSION_CRITICAL.has(g.criticality);
            return (
              <TableRow key={g.key} hover>
                <TableCell sx={{ fontWeight: 600, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {g.key}
                </TableCell>
                {showCriticality && (
                  <TableCell sx={{ fontSize: 12, color: crit ? RED : GREY, fontWeight: crit ? 700 : 400 }}>
                    {g.criticality || 'not recorded'}
                  </TableCell>
                )}
                <TableCell align="right">{fmt(g.total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: g[CLASSES.EOS] ? 700 : 400, color: g[CLASSES.EOS] ? RED : GREY }}>
                  {fmt(g[CLASSES.EOS])}
                </TableCell>
                <TableCell align="right" sx={{ color: g[CLASSES.Y1] ? AMBER : GREY }}>{fmt(g[CLASSES.Y1])}</TableCell>
                <TableCell align="right" sx={{ color: GREY }}>{fmt(g[CLASSES.Y2])}</TableCell>
                <TableCell align="right" sx={{ color: GREY }}>{fmt(g[CLASSES.OK])}</TableCell>
                <TableCell align="right" sx={{ fontWeight: g[CLASSES.UNKNOWN] ? 700 : 400, color: g[CLASSES.UNKNOWN] ? PURPLE : GREY }}>
                  {fmt(g[CLASSES.UNKNOWN])}
                </TableCell>
                <TableCell align="right" sx={{ color: GREY }}>
                  {pct(g.total ? (g[CLASSES.UNKNOWN] / g.total) * 100 : 0)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: NAVY }}>{g.risk.toFixed(1)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {groups.length > MAX_GROUP_ROWS && (
        <Typography variant="caption" sx={{ color: AMBER, display: 'block', p: 1 }}>
          Showing the {MAX_GROUP_ROWS} highest-risk of {fmt(groups.length)} groups covering {fmt(total)} servers —
          every score above and the CSV export cover all of them.
        </Typography>
      )}
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Obsolescence({ tenantId }) {
  const [servers, setServers] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [tab, setTab] = useState(0);
  const [classFilter, setClassFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  // Evaluated once per mount: every "as of" date on the screen and in the
  // export is the same instant, so no two numbers can be computed against
  // different todays.
  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadErr('');
    Promise.all([
      store.listFactSheets(tenantId, { type: 'ITComponent' }),
      store.listFactSheets(tenantId, { type: 'Deployment' }),
      store.listFactSheets(tenantId, { type: 'Application' }),
    ])
      .then(([its, deps, apps]) => {
        if (!live) return;
        setServers((its || []).filter((fs) => isServerRecord(specsOf(fs))));
        setDeployments(deps || []);
        setApplications(apps || []);
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setLoadErr('Could not load the inventory for this workspace. Reload the page and try again.');
        setLoading(false);
      });
    return () => { live = false; };
  }, [tenantId]);

  const linkage = useMemo(
    () => buildLinkage(servers, deployments, applications),
    [servers, deployments, applications]
  );

  const analysis = useMemo(
    () => analyzeObsolescence(servers, linkage, now),
    [servers, linkage, now]
  );

  const { rows, counts, scores, dateSource, entryHits, vagueReasons, byFamily, byCluster, byDatacenter, byApp } = analysis;

  const criticalFindings = useMemo(
    () => rows
      .filter((r) => MISSION_CRITICAL.has(r.criticality) && (r.cls === CLASSES.EOS || r.cls === CLASSES.Y1))
      .sort((a, b) => CRIT_RANK(b.criticality) - CRIT_RANK(a.criticality) || (a.days || 0) - (b.days || 0)),
    [rows]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (classFilter !== 'All' && r.cls !== classFilter) return false;
      if (sourceFilter !== 'All' && r.source !== sourceFilter) return false;
      if (q && !(`${r.name} ${r.os} ${r.cluster} ${r.datacenter} ${r.appNames}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, classFilter, sourceFilter, search]);

  const pagedRows = useMemo(
    () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleRows, page, rowsPerPage]
  );

  const asOf = isoOf(now);

  // --- Caveat text, reused on screen AND in the export ---------------------
  const caveatLines = useMemo(() => [
    'PRIMORDIA — TECHNOLOGY OBSOLESCENCE / END-OF-SUPPORT RISK.',
    `As of ${asOf}. Every classification below is relative to that date and goes stale from then on.`,
    `Records assessed: ${scores.total}. Assessable (a usable end-of-support date exists): ${scores.assessable}.`,
    `MISSING DATA: ${scores.missingData} records (${scores.missingDataPct.toFixed(1)}% of the estate) could NOT be assessed.`,
    'Those records are classified Unknown. They are NOT supported and NOT safe — they are unread.',
    'Aggregated risk is computed over the ASSESSABLE records only, so it UNDERSTATES the true estate',
    'risk by whatever the missing-data share turns out to contain. Read the two numbers together.',
    'DATE PRECEDENCE: a vendor-supplied end-of-support date on the record always beats Primordia\'s',
    `reference table. Every row states which was used. Vendor dates: ${dateSource.vendor}. Inferred from the OS name: ${dateSource.inferred}. No date: ${dateSource.none}.`,
    `Primordia's reference table is internal working data last reviewed ${REFERENCE_REVIEWED}. It is NOT a`,
    'vendor feed and carries no entitlement information — paid extensions (Microsoft ESU, Red Hat ELS,',
    'SUSE LTSS, Ubuntu Pro/ESM, Oracle extended support) move these dates and are never assumed here.',
    'An administrator should verify every entry before these numbers are quoted to a client.',
    'NO DATE IS EVER GUESSED FROM AN AMBIGUOUS OS STRING. "Windows Server 2016 or later", "Ubuntu Linux"',
    'with no release, "Other (32-bit)" and similar are reported as Unknown, never dated to the middle of a range.',
  ], [asOf, scores, dateSource]);

  const exportColumns = [
    { label: 'Server', value: (r) => r.name },
    { label: 'OS string (verbatim)', value: (r) => r.os },
    { label: 'OS family', value: (r) => r.family },
    { label: 'Reference entry used', value: (r) => r.refLabel },
    { label: 'End of support date used', value: (r) => r.eosIso },
    { label: 'Source of that date', value: (r) => r.sourceLabel },
    { label: 'Vendor field name', value: (r) => r.vendorKey },
    { label: 'Vendor field raw value', value: (r) => r.vendorRaw },
    { label: 'Vendor support status (osSupport, verbatim)', value: (r) => r.osSupport },
    { label: 'Days past end of support', value: (r) => (r.days !== null && r.days < 0 ? Math.abs(r.days) : '') },
    { label: 'Days until end of support', value: (r) => (r.days !== null && r.days >= 0 ? r.days : '') },
    { label: 'Classification', value: (r) => r.cls },
    { label: 'Risk weight', value: (r) => (RISK_WEIGHT[r.cls] === undefined ? 'n/a — unassessable' : RISK_WEIGHT[r.cls]) },
    { label: 'Mitigation recorded', value: (r) => (r.mitigated ? 'Yes' : 'No') },
    { label: 'Mitigation evidence', value: (r) => r.mitigationNote },
    { label: 'Linked applications', value: (r) => r.appNames },
    { label: 'Highest business criticality', value: (r) => r.criticality },
    { label: 'Cluster', value: (r) => r.cluster },
    { label: 'Datacenter', value: (r) => r.datacenter },
    { label: 'Host', value: (r) => r.host },
    { label: 'Power state', value: (r) => r.powerState },
    { label: 'Source system', value: (r) => r.sourceSystem },
    { label: 'Evidence — where this date came from', value: (r) => r.evidence },
    { label: 'As of', value: () => asOf },
  ];

  const exportEvidence = () => downloadCsvWithBanner(
    `obsolescence-evidence-${tenantId}-${asOf}.csv`, caveatLines, exportColumns, visibleRows
  );

  const exportAll = () => downloadCsvWithBanner(
    `obsolescence-evidence-all-${tenantId}-${asOf}.csv`, caveatLines, exportColumns, rows
  );

  const exportReference = () => downloadCsvWithBanner(
    `primordia-eol-reference-table-${REFERENCE_REVIEWED}.csv`,
    [
      'PRIMORDIA END-OF-LIFE REFERENCE TABLE — INTERNAL WORKING DATA, NOT A VENDOR FEED.',
      `Last reviewed ${REFERENCE_REVIEWED}. Verify every row against the vendor's own lifecycle page before use.`,
      'Entries marked "cannot be dated" carry NO date on purpose: the OS string they match is too vague',
      'to identify a release, and guessing one would manufacture false confidence.',
      'No entitlement information is modelled. Paid extensions (Microsoft ESU, Red Hat ELS, SUSE LTSS,',
      'Ubuntu Pro/ESM, Oracle extended support) move these dates and are never assumed.',
    ],
    [
      { label: 'Family', value: (e) => e.family },
      { label: 'Entry', value: (e) => e.label },
      { label: 'End of support', value: (e) => e.eos || 'cannot be dated' },
      { label: 'Matches OS strings like', value: (e) => String(e.match) },
      { label: 'Servers matched in this workspace', value: (e) => entryHits.get(e.id) || 0 },
      { label: 'Source / note (verify this)', value: (e) => e.source },
    ],
    EOL_REFERENCE
  );

  const title = (
    <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 0.5 }}>
      Technology Obsolescence &amp; End-of-Support Risk
    </Typography>
  );

  if (loading) {
    return <Box sx={{ p: 2 }}>{title}<LinearProgress sx={{ mt: 2 }} /></Box>;
  }

  if (loadErr) {
    return <Box sx={{ p: 2 }}>{title}<Alert severity="error" sx={{ mt: 2, maxWidth: 820 }}>{loadErr}</Alert></Box>;
  }

  if (!rows.length) {
    return (
      <Box sx={{ p: 2 }}>
        {title}
        <Alert severity="info" sx={{ mt: 2, maxWidth: 900 }}>
          <AlertTitle>No server inventory in this workspace</AlertTitle>
          Load an RVTools export, a CMDB extract or an equivalent inventory from the Infrastructure Import screen
          first. Obsolescence scoring needs, at minimum, an operating-system string per server — and to avoid a
          large missing-data share it needs that string to name a specific release, or a vendor end-of-support
          date captured alongside it.
        </Alert>
      </Box>
    );
  }

  const missingHigh = scores.missingDataPct >= 20;

  return (
    <Box sx={{ p: 2 }}>
      {title}
      <Typography sx={{ fontSize: 13, color: GREY, mb: 2, maxWidth: 1000 }}>
        Obsolescence scored as of <b>{asOf}</b> across <b>{fmt(scores.total)}</b> server records. Four numbers carry
        this screen: aggregated risk, mitigated risk %, unaddressed risk % and — the one most reports leave out —
        missing data %, which scores how much of the risk picture is not yet visible instead of implying full
        coverage.
      </Typography>

      {/* =================================================================== */}
      {/* 1. THE MISSING-DATA GATE — first, and unmissable                    */}
      {/* =================================================================== */}
      <Alert
        severity={missingHigh ? 'warning' : 'info'}
        icon={<HelpOutlineIcon />}
        sx={{ mb: 2, maxWidth: 1150 }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>
          {pct(scores.missingDataPct)} of this estate cannot be assessed — {fmt(scores.missingData)} of{' '}
          {fmt(scores.total)} servers
        </AlertTitle>
        <Typography sx={{ fontSize: 13, mb: 1 }}>
          Those records carry no vendor end-of-support date <b>and</b> an OS string too vague to date. They are
          classified <b>Unknown</b> and they are never folded into &quot;Supported&quot;. An unassessable server is
          not a safe server — it is an unread one, and it is the single most likely place for a 2012-era box to be
          hiding.
        </Typography>
        <Typography sx={{ fontSize: 13, mb: 1 }}>
          Everything else on this screen — aggregated risk included — is computed over the{' '}
          <b>{fmt(scores.assessable)}</b> assessable records only. That makes aggregated risk an{' '}
          <b>understatement</b> by whatever this {pct(scores.missingDataPct)} turns out to contain. Read the two
          numbers side by side, always.
        </Typography>
        {vagueReasons.length > 0 && (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Why each record could not be dated:</Typography>
            <Box component="ul" sx={{ fontSize: 12.5, pl: 2.5, m: 0 }}>
              {vagueReasons.slice(0, 8).map((v) => (
                <li key={v.why}>
                  <b>{fmt(v.n)}</b> — {v.why}
                </li>
              ))}
            </Box>
          </>
        )}
        <Typography sx={{ fontSize: 12.5, mt: 1, fontWeight: 700 }}>
          What to collect: the OS <i>release</i> rather than a family (&quot;Windows Server 2019&quot;, not
          &quot;2016 or later&quot;; &quot;Ubuntu 20.04 LTS&quot;, not &quot;Ubuntu Linux&quot;), captured from the
          guest itself (WMI / <code>lsb_release</code> / an agent) rather than from the hypervisor&apos;s guest-OS
          descriptor, which records what the VM was <i>configured</i> as and not what it <i>runs</i>.
        </Typography>
      </Alert>

      {/* =================================================================== */}
      {/* 2. THE FOUR SCORES                                                  */}
      {/* =================================================================== */}
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15, mb: 1 }}>
        The four obsolescence scores — each defined in plain words
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5, mb: 1.5 }}>
        <ScoreCard
          label="Aggregated risk"
          value={`${scores.aggregatedRisk.toFixed(1)} (${pct(scores.aggregatedRiskPct)})`}
          accent={RED}
          definition={
            `Total weight of obsolescence carried by the ${fmt(scores.assessable)} records that CAN be assessed. `
            + 'Each contributes 1.00 if already past end of support, 0.60 if ending within 12 months, 0.30 if '
            + 'ending within 24 months, 0.00 if supported. The percentage is that total against the worst case '
            + 'for the same set (every assessable server past end of support = 100%).'
          }
          footnote={`Excludes the ${fmt(scores.missingData)} unassessable records entirely — so it is a floor, not a total.`}
        />
        <ScoreCard
          label="Mitigated risk"
          value={pct(scores.mitigatedPct)}
          accent={scores.mitigatedPct > 0 ? GREEN : GREY}
          definition={
            'Share of the aggregated risk above that sits on servers with a remediation actually recorded in this '
            + 'workspace — a migration wave other than "Not Scheduled", a planned cutover or decommission date, a '
            + 'lifecycle state of Phase Out / End of Life, or a named replacement, on the server or on a deployment it hosts.'
          }
          footnote={scores.mitigationFieldsSeen === 0
            ? 'ZERO records carry any mitigation field — this is 0% because nothing is RECORDED, not because nothing is planned.'
            : `${fmt(scores.mitigationFieldsSeen)} records carry at least one mitigation field.`}
        />
        <ScoreCard
          label="Unaddressed risk"
          value={pct(scores.unaddressedPct)}
          accent={scores.unaddressedPct > 50 ? RED : AMBER}
          definition={
            'The remainder: aggregated risk with no remediation recorded anywhere in the model. This is the number '
            + 'that belongs in a remediation plan and a budget line. It is bounded by what the CMDB knows — a plan '
            + 'that exists only in someone\'s head counts as unaddressed here, correctly.'
          }
          footnote={`${fmt(scores.atRisk)} servers carry non-zero risk weight.`}
        />
        <ScoreCard
          label="Missing data"
          value={pct(scores.missingDataPct)}
          accent={PURPLE}
          definition={
            `Share of ALL ${fmt(scores.total)} records whose support status cannot be determined at all: no vendor `
            + 'end-of-support date and an OS string too vague to date. This is the honest measure of how much of '
            + 'the risk picture is still invisible. A low aggregated risk next to a high missing-data % means '
            + '"we have not looked yet", not "we are fine".'
          }
          footnote={missingHigh ? 'HIGH — the other three scores cover only part of the estate.' : null}
        />
      </Box>

      <Paper sx={{ p: 1.25, mb: 2, maxWidth: 1150 }}>
        <Typography sx={{ fontSize: 12.5, color: GREY }}>
          <b style={{ color: NAVY }}>What counts as a mitigation here, exactly:</b>{' '}
          {MITIGATION_SIGNALS.join('; ')} — recorded on the server fact sheet itself or on a Deployment it hosts.
          Nothing else counts, and nothing is inferred. A migration wave of &quot;Not Scheduled&quot; is explicitly
          <b> not</b> a plan. Mitigated risk is therefore a measure of what this workspace can <i>evidence</i>, and a
          remediation that exists only in a project tracker will read as unaddressed until it is recorded here —
          which is the correct answer for a CMDB to give.
        </Typography>
      </Paper>

      {/* =================================================================== */}
      {/* 3. CLASSIFICATION                                                   */}
      {/* =================================================================== */}
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15, mb: 1 }}>
        Support status as of {asOf}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))', gap: 1.5, mb: 1.5 }}>
        {CLASS_ORDER.map((c) => (
          <Paper key={c} sx={{ p: 1.25, borderTop: `3px solid ${CLASS_COLOR[c]}` }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{c}</Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
              {fmt(counts[c])}
            </Typography>
            <Typography sx={{ fontSize: 12, color: GREY, fontWeight: 700 }}>
              {pct(scores.total ? (counts[c] / scores.total) * 100 : 0)} of the estate
            </Typography>
            <Typography sx={{ fontSize: 11, color: GREY }}>{CLASS_NOTE[c]}</Typography>
          </Paper>
        ))}
      </Box>

      {/* =================================================================== */}
      {/* 4. DATE PRECEDENCE — the two paths, never blurred                   */}
      {/* =================================================================== */}
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15, mb: 1 }}>
        Where each date came from
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5, maxWidth: 1150 }}>
        <AlertTitle sx={{ fontWeight: 800 }}>Precedence: a vendor date always beats an inferred one</AlertTitle>
        <Typography sx={{ fontSize: 13, mb: 1 }}>
          When the record carries its own end-of-support date (field <code>osEos</code> or an equivalent), that date
          is used and the reference table is ignored for that row. Only when no vendor date exists does Primordia
          infer one from the OS name — and it says so, per row, on screen and in the export. A client <i>will</i>
          {' '}challenge these numbers; every row can answer &quot;which field did that come from?&quot;.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
            label={`Vendor date on the record: ${fmt(dateSource.vendor)}`}
            sx={{ fontWeight: 700, bgcolor: 'transparent', border: `1px solid ${GREEN}`, color: GREEN }} />
          <Chip size="small"
            label={`Inferred from the OS name: ${fmt(dateSource.inferred)}`}
            sx={{ fontWeight: 700, bgcolor: 'transparent', border: `1px solid ${BLUE}`, color: BLUE }} />
          <Chip size="small"
            label={`No usable date at all: ${fmt(dateSource.none)}`}
            sx={{ fontWeight: 700, bgcolor: 'transparent', border: `1px solid ${PURPLE}`, color: PURPLE }} />
          {dateSource.unparseable > 0 && (
            <Chip size="small"
              label={`Vendor field present but unreadable as a date: ${fmt(dateSource.unparseable)}`}
              sx={{ fontWeight: 700, bgcolor: '#FFF4E5', color: AMBER }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: 12, color: GREY, mt: 1 }}>
          {dateSource.vendor === 0
            ? 'No record in this workspace carries a vendor end-of-support date. Every date below is inferred from the OS name and inherits every limitation of that string.'
            : dateSource.inferred === 0
              ? 'Every dated record here carries a vendor-supplied date; nothing below is inferred.'
              : 'This workspace mixes both paths. The per-row label is the only reliable guide — do not read the table as if one rule applied throughout.'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: GREY, mt: 0.5 }}>
          A vendor <code>osSupport</code> status string is shown verbatim in the evidence table but never drives a
          classification: a status has no as-of date, so it goes stale silently. Dates are the only thing scored.
        </Typography>
      </Alert>

      {/* =================================================================== */}
      {/* 5. MISSION-CRITICAL FINDING                                          */}
      {/* =================================================================== */}
      {criticalFindings.length > 0 ? (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2, maxWidth: 1150 }}>
          <AlertTitle sx={{ fontWeight: 800 }}>
            {fmt(criticalFindings.length)} unsupported or imminently-unsupported servers sit under a
            business-critical or mission-critical application
          </AlertTitle>
          <Typography sx={{ fontSize: 13, mb: 1 }}>
            This, not the raw count, is the finding. A thousand end-of-support workstations are a patching backlog;
            one end-of-support database server under a mission-critical clinical application is an incident waiting
            for a date.
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Application</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Criticality</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>OS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>EOS date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date source</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mitigation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {criticalFindings.slice(0, MAX_GROUP_ROWS).map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.appNames}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: RED, fontWeight: 700 }}>{r.criticality}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: GREY }}>{r.os}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.eosIso || DASH}</TableCell>
                    <TableCell><SourceChip source={r.source} label={r.sourceLabel} /></TableCell>
                    <TableCell sx={{ fontSize: 11, color: r.mitigated ? GREEN : RED, fontWeight: 700 }}>
                      {r.mitigated ? r.mitigationNote : 'none recorded'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {criticalFindings.length > MAX_GROUP_ROWS && (
              <Typography variant="caption" sx={{ color: AMBER, display: 'block', p: 1 }}>
                Showing {MAX_GROUP_ROWS} of {fmt(criticalFindings.length)} — the CSV export covers all of them.
              </Typography>
            )}
          </TableContainer>
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 1150 }}>
          <AlertTitle sx={{ fontWeight: 800 }}>No server-to-application linkage to report against</AlertTitle>
          <Typography sx={{ fontSize: 13 }}>
            {byApp.length === 0
              ? 'Not one server in this workspace is linked to an Application or a Deployment, so obsolescence cannot be expressed as business impact — only as a count of boxes. '
              : 'No end-of-support or imminently-expiring server is currently linked to a Business Critical or Mission Critical application. '}
            {fmt(scores.unlinked)} at-risk or unassessable servers carry no application link at all. Until that
            mapping exists, &quot;{fmt(counts[CLASSES.EOS])} servers past end of support&quot; cannot be turned into
            &quot;these clinical services are exposed&quot;, and the second sentence is the one that funds
            remediation.
          </Typography>
        </Alert>
      )}

      {/* =================================================================== */}
      {/* 6. BREAKDOWNS                                                       */}
      {/* =================================================================== */}
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15, mb: 0.5 }}>
        Breakdowns
      </Typography>
      <Typography sx={{ fontSize: 12, color: GREY, mb: 1, maxWidth: 1000 }}>
        Every breakdown carries its own Unknown column and missing-data %, because coverage is never uniform: one
        cluster can be fully described while another is entirely undated, and an estate-level average hides exactly
        that. Groups are ordered by aggregated risk.
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1, minHeight: 36 }}>
        <Tab label={`OS family (${byFamily.length})`} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 700 }} />
        <Tab label={`Cluster (${byCluster.length})`} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 700 }} />
        <Tab label={`Datacenter (${byDatacenter.length})`} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 700 }} />
        <Tab label={`Application (${byApp.length})`} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 700 }} />
      </Tabs>
      <Box sx={{ mb: 2 }}>
        {tab === 0 && <GroupTable groups={byFamily} keyLabel="OS family" total={scores.total} />}
        {tab === 1 && <GroupTable groups={byCluster} keyLabel="Cluster" total={scores.total} />}
        {tab === 2 && <GroupTable groups={byDatacenter} keyLabel="Datacenter" total={scores.total} />}
        {tab === 3 && (
          byApp.length === 0 ? (
            <Alert severity="warning">
              No server in this workspace is linked to an Application, directly or through a Deployment. Build that
              linkage in Server Mapping — an unsupported OS under a mission-critical application is the finding that
              matters, and without the link this screen can only count boxes.
            </Alert>
          ) : (
            <>
              <GroupTable groups={byApp} keyLabel="Application" showCriticality total={scores.total} />
              <Typography sx={{ fontSize: 11.5, color: AMBER, mt: 0.5 }}>
                A server shared by several applications is counted under each of them, so these group totals sum to
                more than the estate. {fmt(scores.unlinked)} at-risk or unassessable servers appear in no group here
                because they have no application link — they are not absent from the risk, only from the attribution.
              </Typography>
            </>
          )
        )}
      </Box>

      {/* =================================================================== */}
      {/* 7. THE REFERENCE TABLE ITSELF                                       */}
      {/* =================================================================== */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MenuBookIcon sx={{ fontSize: 18, color: BLUE }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15 }}>
              Primordia end-of-life reference table — {EOL_REFERENCE.length} entries, last reviewed {REFERENCE_REVIEWED}
            </Typography>
            <Chip size="small" label="verify before quoting" sx={{ height: 19, fontSize: 10.5, fontWeight: 700, bgcolor: '#FFF4E5', color: AMBER }} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <AlertTitle sx={{ fontWeight: 800 }}>This is Primordia&apos;s working data, not vendor truth</AlertTitle>
            <Typography sx={{ fontSize: 13 }}>
              These dates were assembled from published vendor lifecycle pages and are maintained by hand. They are
              <b> not</b> a live vendor feed, and they model <b>no entitlements</b> — Microsoft ESU, Red Hat ELS,
              SUSE LTSS, Ubuntu Pro/ESM and Oracle extended support all move these dates for money, per server, and
              are never assumed here. An administrator should verify each entry that matters against the vendor&apos;s
              own page before any of it is quoted to a client. Entries showing <b>cannot be dated</b> carry no date
              deliberately.
            </Typography>
          </Alert>
          <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportReference} sx={{ mb: 1.5 }}>
            Export reference table for review ({EOL_REFERENCE.length} entries)
          </Button>
          <TableContainer component={Paper} sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Family</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Entry</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>End of support</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Matched here</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 340 }}>Source note — verify this</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {EOL_REFERENCE.map((e) => {
                  const hits = entryHits.get(e.id) || 0;
                  return (
                    <TableRow key={e.id} hover sx={{ bgcolor: hits ? undefined : 'transparent', opacity: hits ? 1 : 0.72 }}>
                      <TableCell sx={{ fontSize: 12, color: GREY }}>{e.family}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12.5 }}>{e.label}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: e.eos ? NAVY : PURPLE }}>
                        {e.eos || 'cannot be dated'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: hits ? 700 : 400, color: hits ? NAVY : GREY }}>
                        {hits ? fmt(hits) : DASH}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: GREY }}>{e.source}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ fontSize: 11.5, color: GREY, mt: 1 }}>
            Before the table is consulted at all, five structural guards reject OS strings that cannot name a
            release: {VAGUE_RULES.map((v) => v.why).join('; ')}. Anything caught by one of those is Unknown by
            construction, never dated.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* =================================================================== */}
      {/* 8. PER-SERVER EVIDENCE                                              */}
      {/* =================================================================== */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15 }}>
          Per-server evidence
        </Typography>
        <TextField
          select size="small" label="Classification" value={classFilter}
          onChange={(e) => { setClassFilter(e.target.value); setPage(0); }}
          sx={{ width: 250 }}
        >
          <MenuItem value="All">All ({fmt(rows.length)})</MenuItem>
          {CLASS_ORDER.map((c) => (
            <MenuItem key={c} value={c}>{c} ({fmt(counts[c])})</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Date source" value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
          sx={{ width: 230 }}
        >
          <MenuItem value="All">All sources</MenuItem>
          <MenuItem value="vendor">Vendor date ({fmt(dateSource.vendor)})</MenuItem>
          <MenuItem value="inferred">Inferred from OS name ({fmt(dateSource.inferred)})</MenuItem>
          <MenuItem value="none">No date ({fmt(dateSource.none)})</MenuItem>
        </TextField>
        <TextField
          size="small" label="Search server / OS / cluster / app" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 280 }}
        />
        <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportEvidence}>
          CSV, current filter ({fmt(visibleRows.length)})
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportAll}>
          CSV, all {fmt(rows.length)} servers
        </Button>
      </Stack>
      <Typography sx={{ fontSize: 11.5, color: AMBER, fontWeight: 700, mb: 1 }}>
        Both exports carry the missing-data figure and the date-precedence rule in their header — do not strip it.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 210 }}>OS string (verbatim)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date used</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source of that date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Days past /<br />until EOS</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Classification</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Application</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Mitigation</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 300 }}>Evidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  {r.name}
                  {r.powerState && !/^powered ?on$/i.test(r.powerState) && (
                    <Chip size="small" label={r.powerState}
                      sx={{ ml: 0.5, height: 17, fontSize: 10, bgcolor: '#FFF4E5', color: AMBER, fontWeight: 700 }} />
                  )}
                  {(r.cluster || r.datacenter) && (
                    <Typography sx={{ fontSize: 10.5, color: GREY }}>
                      {[r.datacenter, r.cluster].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: GREY }}>
                  {r.os || <i>not recorded</i>}
                  {r.osSupport && (
                    <Tooltip title="Vendor osSupport status, shown verbatim. Not used to classify — a status string has no as-of date.">
                      <Chip size="small" label={r.osSupport}
                        sx={{ ml: 0.5, height: 16, fontSize: 9.5, bgcolor: '#EEF3F8', color: NAVY }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.eosIso || <span style={{ color: PURPLE }}>none</span>}</TableCell>
                <TableCell><SourceChip source={r.source} label={r.sourceLabel} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: r.days === null ? GREY : r.days < 0 ? RED : r.days <= 365 ? AMBER : GREY }}>
                  {r.days === null ? DASH : r.days < 0 ? `${fmt(Math.abs(r.days))} past` : `${fmt(r.days)} left`}
                </TableCell>
                <TableCell><ClassChip cls={r.cls} small /></TableCell>
                <TableCell sx={{ fontSize: 11.5, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.appNames || <span style={{ color: GREY }}>not linked</span>}
                  {MISSION_CRITICAL.has(r.criticality) && (
                    <Chip size="small" label={r.criticality}
                      sx={{ ml: 0.5, height: 16, fontSize: 9.5, bgcolor: 'transparent', border: `1px solid ${RED}`, color: RED, fontWeight: 700 }} />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 11, color: r.mitigated ? GREEN : GREY, fontWeight: r.mitigated ? 700 : 400 }}>
                  {r.mitigated ? r.mitigationNote : 'none recorded'}
                </TableCell>
                <TableCell sx={{ fontSize: 11, color: GREY }}>{r.evidence}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={visibleRows.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={PAGE_SIZES}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      <Typography sx={{ fontSize: 11, color: GREY, mt: 1, mb: 3, maxWidth: 1150 }}>
        Rendered rows are capped and paged; every score, count, breakdown and export above covers the full set of{' '}
        {fmt(rows.length)} server records. Scored as of <b>{asOf}</b> — the classification of a server ending
        support inside the next month changes tomorrow, so re-run this rather than reusing a screenshot. No date on
        this screen is guessed from an ambiguous OS string, no inferred date is presented as vendor-confirmed, and
        the {pct(scores.missingDataPct)} that cannot be assessed is reported rather than absorbed.
      </Typography>
      <Divider />
    </Box>
  );
}
